import { useChangePaymentMutation } from './useChangePaymentInCart';
import { handleCartModifications, useCurrentCartInfo } from 'connectors/cart/Cart';
import { getCookies } from 'cookies-next';
import { CartInfoQueryApi, CartInfoQueryVariablesApi, CartQueryDocumentApi } from 'graphql/generated';
import { getUrlWithoutGetParameters } from 'helpers/parsing/urlParsing';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useBroadcastChannel } from 'hooks/useBroadcastChannel';
import { useCurrentUserData } from 'hooks/user/useCurrentUserData';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { useSessionStore } from 'store/useSessionStore';
import { useClient } from 'urql';

export const useReloadCart = (): void => {
    const { modifications, refetchCart } = useCurrentCartInfo(false);
    const t = useTypedTranslationFunction();
    const router = useRouter();
    const slug = getUrlWithoutGetParameters(router.asPath);
    const { isUserLoggedIn } = useCurrentUserData();
    const [changePaymentInCart] = useChangePaymentMutation();
    const updateLastVisitedSlug = useSessionStore((store) => store.updateLastVisitedSlug);
    const lastVisitedSlug = useSessionStore((store) => store.lastVisitedSlug);
    const client = useClient();
    const { cartUuid, defaultStoreUuid, selectedStoreUuid } = usePersistStore((s) => s);

    useBroadcastChannel({
        name: 'refetchCart',
        callBack: () => {
            const cookies = getCookies();
            const isWithUserTokens = !!(cookies.accessToken && cookies.refreshToken);

            if ((isUserLoggedIn && !isWithUserTokens) || (!isUserLoggedIn && isWithUserTokens)) {
                router.reload();
            } else if (cartUuid || isUserLoggedIn) {
                refetchCart();
            }
        },
    });

    useEffect(() => {
        const hasSlugChanged = lastVisitedSlug !== slug;
        updateLastVisitedSlug(slug);

        if ((cartUuid || isUserLoggedIn) && hasSlugChanged) {
            client
                .query<CartInfoQueryApi, CartInfoQueryVariablesApi>(
                    CartQueryDocumentApi,
                    { cartUuid, defaultStoreUuid, selectedStoreUuid },
                    { requestPolicy: 'network-only' },
                )
                .toPromise();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    useEffect(() => {
        if (modifications !== null) {
            handleCartModifications(modifications, t, changePaymentInCart);
        }
    }, [changePaymentInCart, modifications, t]);
};
